import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

interface SubmitButtonProps extends Omit<ButtonProps, 'onClick'> {
  isSubmitting?: boolean;
  children: React.ReactNode;
  loadingText?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  useDefaultStyle?: boolean;
}

const StyledButton = styled(Button)<ButtonProps>(({ theme }) => ({
  position: 'relative',
  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

const DefaultStyledButton = styled(Button)<ButtonProps>(({ theme }) => ({
  position: 'relative',
  width: '175px',
  height: '40px',
  backgroundColor: '#1e88e5',
  color: '#ffffff',
  fontSize: '12px',
  fontFamily: 'IRANYekanXFaNum',
  fontWeight: 700,
  padding: '12px 16px',
  borderRadius: '4px',
  textTransform: 'none',
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
  direction: 'ltr',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#1565c0',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    backgroundColor: '#bdbdbd',
    color: '#757575',
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px',
    marginLeft: '0px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiButton-endIcon': {
    marginLeft: '8px',
    marginRight: '0px',
    display: 'flex',
    alignItems: 'center',
  },
}));

const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  marginLeft: -12,
  marginTop: -12,
  color: '#ffffff',
}));

const SubmitButton: React.FC<SubmitButtonProps> = ({
  isSubmitting = false,
  children,
  loadingText = 'در حال پردازش...',
  disabled,
  onClick,
  useDefaultStyle = true,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  const ButtonComponent = useDefaultStyle ? DefaultStyledButton : StyledButton;

  return (
    <ButtonComponent
      {...props}
      disabled={disabled || isSubmitting}
      onClick={handleClick}
      startIcon={isSubmitting ? <LoadingSpinner size={20} /> : props.startIcon}
    >
      {isSubmitting ? loadingText : children}
    </ButtonComponent>
  );
};

export default SubmitButton;
